import * as THREE from 'three';
import { G, mesh } from '../render/scene';
import { MAT_P, coreGlow } from '../render/materials';
import { bevel, cable, mark, plate, ram, rib, seam } from './mech-kit';
/* ============================================================
   LANCER-2 · 核心 CORE：軀幹 + 雙肩承座
   ============================================================

   核心比例（local y0 = 腰線最窄處）：
     肩承座 x ±1.02　胸寬 1.94　腰寬 0.74　胸腰比 **2.6**（CHASSIS-0 是 2.5）
     腰線→肩 1.00（CHASSIS-0 是 0.94）

   **胸腔要比胸甲窄。** 反過來的話兩片胸甲會連成一面牆、把頭埋掉，
   而且手臂會黏在軀幹上 —— 第一輪就是這樣（shots/lancer/ 第二輪）。

   **軀幹比 CHASSIS-0 長、腿比它短** —— 這不是矛盾，這正是健體體型的
   定義：長軀幹、短四肢、極端的 V 錐。全高反而矮 13%，因為腿只佔 47%。

   V 錐靠三層疊出來，不是靠一塊板：
     ① 闊背板從肩往內下收（外層，決定剪影）
     ② 胸甲兩片各自外傾（中層，決定受光面）
     ③ 腰段只有 0.74 寬且看得穿（內層，決定對比）
   少任何一層都會讀成「一個上寬下窄的箱子」。 */

export function buildLancerCore(shape, P?) {
  P = P || { glow: coreGlow(shape) };
  var g = new THREE.Group();
  mark(g, shape, ['core-crit', 'core-burn', 'core-std']);

  /* 腰關節：與 CHASSIS-0 同一套機構語彙（胸與骨盆之間要看得穿），
     但這台的腰更細更長 —— 扭腰是它的主要輸出動作，腰要看得出行程。 */
  g.add(mesh(G.cyl, MAT_P.trim, [0.34, 0.56, 0.34], [0, 0.02, 0]));
  g.add(mesh(G.tor, MAT_P.trim, [0.62, 0.62, 0.22], [0, 0.26, 0], [Math.PI / 2, 0, 0]));
  g.add(ram([0.28, 0.04, 0.18], 0.50, 0.11, 0.09, [-0.16, 0, 0]));            // 前液壓 ×2
  g.add(ram([-0.28, 0.04, 0.18], 0.50, 0.11, 0.09, [-0.16, 0, 0]));
  g.add(ram([0.22, 0.04, -0.20], 0.50, 0.10, 0.08, [0.18, 0, 0]));            // 後液壓（近戰要能後仰蓄力）
  g.add(cable([0.30, 0.18, -0.24], 0.36));

  // 共用的軀幹主體：胸腔 + 闊背 + 腹 + 肩承座（三種核心只改胸口與背面）
  g.add(bevel(MAT_P.dark, 1.42, 1.02, 1.00, [0, 0.62, 0]));                        // 胸腔內部（比胸甲窄）
  g.add(plate(MAT_P.armor, 0.32, 1.02, 0.84, [0.76, 0.48, 0.02], 0, -0.42, -0.26));// 闊背 → V 錐
  g.add(plate(MAT_P.armor, 0.32, 1.02, 0.84, [-0.76, 0.48, 0.02], 0, 0.42, 0.26));
  g.add(rib(MAT_P.armor, 0.09, 1.00, 0.10, [0.85, 0.50, 0.10], 0, -0.42, -0.26));  // 闊背外緣亮線
  g.add(rib(MAT_P.armor, 0.09, 1.00, 0.10, [-0.85, 0.50, 0.10], 0, 0.42, 0.26));
  g.add(plate(MAT_P.armor, 0.82, 0.32, 0.30, [0, 0.20, 0.40], 0.34));              // 腹甲（收窄）
  g.add(bevel(MAT_P.dark, 0.52, 0.70, 0.74, [1.02, 1.00, 0]));                     // 肩承座（寬）
  g.add(bevel(MAT_P.dark, 0.52, 0.70, 0.74, [-1.02, 1.00, 0]));
  g.add(plate(MAT_P.armor, 0.70, 0.24, 0.60, [0.54, 1.14, 0], 0, -0.16, -0.34));   // 斜方肌（厚、上揚）
  g.add(plate(MAT_P.armor, 0.70, 0.24, 0.60, [-0.54, 1.14, 0], 0, 0.16, 0.34));
  g.add(rib(MAT_P.armor, 0.74, 0.09, 0.09, [0.54, 1.25, 0.10], 0, -0.16, -0.34));  // 肩線亮線
  g.add(rib(MAT_P.armor, 0.74, 0.09, 0.09, [-0.54, 1.25, 0.10], 0, 0.16, 0.34));

  if (shape === 'core-crit') {
    /* 精準核心：胸口開一道外露的稜柱，兩側裝甲往內夾 —— 讀起來像
       「把能量集中到一個點」，與暴擊的語意對得上。 */
    g.add(mesh(G.bev, P.glow, [0.38, 0.92, 0.38], [0, 0.66, 0.40]));
    g.add(plate(MAT_P.armor, 0.70, 0.96, 0.30, [0.58, 0.68, 0.42], -0.12, -0.40, -0.12));
    g.add(plate(MAT_P.armor, 0.70, 0.96, 0.30, [-0.58, 0.68, 0.42], -0.12, 0.40, 0.12));
    g.add(rib(MAT_P.armor, 0.10, 0.96, 0.14, [0.34, 0.70, 0.56], -0.12, -0.40, -0.12));
    g.add(rib(MAT_P.armor, 0.10, 0.96, 0.14, [-0.34, 0.70, 0.56], -0.12, 0.40, 0.12));
    g.add(mesh(G.cone, MAT_P.armor, [0.30, 0.86, 0.30], [0, 1.32, -0.24], [-0.26, 0, 0]));
    g.add(plate(MAT_P.armor, 1.28, 0.52, 0.28, [0, 0.88, -0.46], 0.12));

  } else if (shape === 'core-burn') {
    /* 過載核心：大面積發光 + 三片高過肩線的散熱鰭。近戰機體貼著敵人打，
       散熱鰭是它唯一「往外長」的部件，那個氣場是刻意的。 */
    g.add(plate(MAT_P.armor, 0.94, 0.44, 0.34, [0.50, 0.96, 0.40], -0.32, -0.10, -0.16));
    g.add(plate(MAT_P.armor, 0.94, 0.44, 0.34, [-0.50, 0.96, 0.40], -0.32, 0.10, 0.16));
    g.add(rib(MAT_P.armor, 0.13, 0.50, 0.16, [0, 0.96, 0.52], -0.32));
    g.add(mesh(G.box, P.glow, [1.30, 0.20, 0.12], [0, 0.68, 0.52]));
    g.add(mesh(G.box, P.glow, [0.92, 0.14, 0.10], [0, 0.42, 0.48]));
    g.add(seam(1.40, 0.06, 0.10, [0, 0.82, 0.46], 0.10));
    g.add(plate(MAT_P.armor, 1.44, 0.12, 0.56, [0, 1.16, -0.42], 0.62));            // 散熱鰭 ×3
    g.add(plate(MAT_P.armor, 1.36, 0.12, 0.56, [0, 0.86, -0.48], 0.62));
    g.add(plate(MAT_P.armor, 1.26, 0.12, 0.56, [0, 0.56, -0.54], 0.62));
    g.add(mesh(G.cyl, MAT_P.trim, [0.30, 0.92, 0.30], [0, 0.64, -0.30]));

  } else {  /* core-std：兩片大胸甲 + 中央龍骨 + 一顆環形核心 */
    /* 胸甲外緣往下垂（roll ∓0.18）—— 那一下把兩片矩形變成胸肌的形狀。
       沒有 roll 的話正面就是兩塊等高的板子，怎麼調大小都還是牆。 */
    g.add(plate(MAT_P.armor, 0.84, 0.62, 0.38, [0.52, 0.82, 0.44], -0.26, -0.14, -0.18));
    g.add(plate(MAT_P.armor, 0.84, 0.62, 0.38, [-0.52, 0.82, 0.44], -0.26, 0.14, 0.18));
    g.add(rib(MAT_P.armor, 0.13, 0.90, 0.18, [0, 0.80, 0.54], -0.26));              // 中央龍骨（亮線）
    g.add(seam(1.30, 0.05, 0.10, [0, 0.42, 0.46], 0.12));                       // 胸下縫
    g.add(mesh(G.cyl, P.glow, [0.32, 0.14, 0.32], [0, 0.60, 0.54], [Math.PI / 2, 0, 0]));
    g.add(mesh(G.tor, MAT_P.trim, [0.56, 0.56, 0.30], [0, 0.60, 0.54]));
    g.add(plate(MAT_P.armor, 1.30, 0.52, 0.28, [0, 0.88, -0.46], 0.12));            // 背板上段
    g.add(plate(MAT_P.armor, 1.14, 0.42, 0.26, [0, 0.30, -0.50], 0.02));            // 下段：中間留穿透的縫
  }
  return g;
}
